import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { Mail, KeyRound, ArrowRight, UserCheck } from 'lucide-react';
import authService, { LoginDto } from '../../services/auth.service';

const AdherentLogin: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<LoginDto>({
        email: '',
        motdepasse: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Partial<LoginDto>>({});
    const [rememberMe, setRememberMe] = useState(false);

    useEffect(() => {
        const savedEmail = localStorage.getItem('adh_remembered_email');
        if (savedEmail) {
            setFormData(prev => ({ ...prev, email: savedEmail }));
            setRememberMe(true);
        }
    }, []);

    const validate = (): boolean => {
        const newErrors: Partial<LoginDto> = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email est requis';
        }

        if (!formData.motdepasse) {
            newErrors.motdepasse = 'Code adhérent requis';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            await authService.login(formData);

            if (rememberMe) {
                localStorage.setItem('adh_remembered_email', formData.email);
            } else {
                localStorage.removeItem('adh_remembered_email');
            }

            console.log('Login successful, checking profile...');
            const user = authService.getUserProfile();
            console.log('User profile:', user);

            // Verify correct role
            const hasValidRole = user?.roles.some(role =>
                role.toLowerCase() === 'adherent' || role.toLowerCase() === 'admin'
            );

            if (hasValidRole) {
                console.log('Role valid, redirecting...');
                toast.success('Bienvenue dans votre espace adhérent');
                navigate('/adherent/dashboard'); // Redirect to adherent dashboard
            } else {
                console.warn('Invalid role for adherent login:', user?.roles);
                authService.logout();
                toast.error('Accès non autorisé. Ce compte n\'est pas un compte adhérent.');
            }

        } catch (error: any) {
            const errorMessage = error.message || 'Échec de la connexion. Vérifiez votre code adhérent.';
            toast.error(errorMessage);
            setErrors({ motdepasse: errorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name as keyof LoginDto]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center relative bg-white overflow-hidden font-sans">
            {/* Background Elements */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-secondary/5 to-transparent opacity-60"></div>
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative z-10 w-full max-w-md px-4"
            >
                <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-12 border border-gray-50">

                    <div className="text-center mb-8">
                        <motion.div
                            initial={{ y: -10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center justify-center w-16 h-16 bg-secondary/10 rounded-2xl mb-5 text-secondary"
                        >
                            <UserCheck size={32} />
                        </motion.div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Espace Adhérent</h2>
                        <p className="text-gray-500 text-sm">
                            Saisissez votre email et votre code confidentiel pour accéder à votre espace.
                        </p>
                    </div>

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 ml-1">Email</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-secondary transition-colors" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`block w-full pl-11 pr-4 py-3.5 border-0 ring-1 ring-inset ${errors.email ? 'ring-red-300 bg-red-50/10' : 'ring-gray-100 focus:ring-secondary bg-gray-50/30'
                                        } rounded-xl focus:outline-none focus:ring-2 transition-all text-gray-900 placeholder-gray-400`}
                                    placeholder="votre@email.com"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="motdepasse" className="block text-sm font-semibold text-gray-700 ml-1">Code Adhérent</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <KeyRound className="h-5 w-5 text-gray-400 group-focus-within:text-secondary transition-colors" />
                                </div>
                                <input
                                    id="motdepasse"
                                    name="motdepasse"
                                    type="password"
                                    required
                                    value={formData.motdepasse}
                                    onChange={handleChange}
                                    className={`block w-full pl-11 pr-4 py-3.5 border-0 ring-1 ring-inset ${errors.motdepasse ? 'ring-red-300 bg-red-50/10' : 'ring-gray-100 focus:ring-secondary bg-gray-50/30'
                                        } rounded-xl focus:outline-none focus:ring-2 transition-all text-gray-900 placeholder-gray-400 text-lg tracking-widest`}
                                    placeholder="••••••••"
                                />
                            </div>
                            {errors.motdepasse && <p className="text-xs text-red-500 mt-1 ml-1">{errors.motdepasse}</p>}
                        </div>

                        <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="h-4 w-4 text-secondary focus:ring-secondary border-gray-300 rounded cursor-pointer"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600 cursor-pointer">Se souvenir de moi</label>
                            </div>
                            <Link to="/contact" className="text-sm font-medium text-secondary hover:text-secondary-dark transition-colors">
                                Code perdu ?
                            </Link>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full flex justify-center py-4 px-4 rounded-xl text-sm font-bold text-white shadow-lg shadow-secondary/20 transition-all ${isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-secondary hover:bg-secondary-dark hover:shadow-secondary/40 active:scale-[0.98]'
                                }`}
                        >
                            {isLoading ? 'Connexion...' : <span className="flex items-center">Accéder à mon espace <ArrowRight className="ml-2 h-4 w-4" /></span>}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500">
                            Vous n'êtes pas encore membre ?{' '}
                            <Link to="/adhesion" className="font-semibold text-secondary hover:text-secondary-dark">
                                Adhérer maintenant
                            </Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default AdherentLogin;
